import { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { usePeriods, type Period } from '@/hooks/usePeriods';

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const STORAGE_KEY = 'garage-renaissance-period-state';

const saveToStorage = (state: Partial<PeriodState>) => {
  try {
    const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...existing, ...state }));
  } catch (e) {
    console.error('Failed to save period state:', e);
  }
};

const loadFromStorage = (): Partial<PeriodState> | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    return null;
  }
};

interface PeriodState {
  selectedPeriod: string | null;
  selectedYear: number | null;
  selectedMonth: number | null;
  reportsYear: number | null;
  reportsFromMonth: number | null;
  reportsToMonth: number | null;
  activeReportTab: string;
  taxReturnFY: string;
}

// Get current financial year (July to June)
const getCurrentFinancialYear = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  // If month is July (7) or later, FY starts this year
  // If month is before July, FY started last year
  const fyStartYear = month >= 7 ? year : year - 1;
  return `${fyStartYear}-${fyStartYear + 1}`;
};

type PeriodAction =
  | { type: 'INITIALIZE_ALL'; payload: { year: number; month: number } }
  | { type: 'SET_SELECTED_PERIOD'; payload: string | null }
  | { type: 'SET_YEAR'; payload: number | null }
  | { type: 'SET_MONTH'; payload: number | null }
  | { type: 'SET_REPORTS_YEAR'; payload: number | null }
  | { type: 'SET_REPORTS_FROM_MONTH'; payload: number | null }
  | { type: 'SET_REPORTS_TO_MONTH'; payload: number | null }
  | { type: 'SET_ACTIVE_REPORT_TAB'; payload: string }
  | { type: 'SET_TAX_RETURN_FY'; payload: string };

const periodReducer = (state: PeriodState, action: PeriodAction): PeriodState => {
  switch (action.type) {
    case 'INITIALIZE_ALL':
      return {
        ...state,
        selectedYear: action.payload.year,
        selectedMonth: action.payload.month,
        reportsYear: action.payload.year,
        reportsFromMonth: action.payload.month,
        reportsToMonth: action.payload.month,
      };
    case 'SET_SELECTED_PERIOD':
      return { ...state, selectedPeriod: action.payload };
    case 'SET_YEAR':
      return { ...state, selectedYear: action.payload };
    case 'SET_MONTH':
      return { ...state, selectedMonth: action.payload };
    case 'SET_REPORTS_YEAR':
      return { ...state, reportsYear: action.payload };
    case 'SET_REPORTS_FROM_MONTH':
      return { ...state, reportsFromMonth: action.payload };
    case 'SET_REPORTS_TO_MONTH':
      return { ...state, reportsToMonth: action.payload };
    case 'SET_ACTIVE_REPORT_TAB':
      return { ...state, activeReportTab: action.payload };
    case 'SET_TAX_RETURN_FY':
      return { ...state, taxReturnFY: action.payload };
    default:
      return state;
  }
};

interface PeriodContextType extends PeriodState {
  periods: Period[];
  periodsLoading: boolean;
  refreshPeriods: () => void;
  setSelectedPeriod: (id: string | null) => void;
  setYear: (year: number | null) => void;
  setMonth: (month: number | null) => void;
  setReportsYear: (year: number | null) => void;
  setReportsFromMonth: (month: number | null) => void;
  setReportsToMonth: (month: number | null) => void;
  setActiveReportTab: (tab: string) => void;
  setTaxReturnFY: (fy: string) => void;
}

const PeriodContext = createContext<PeriodContextType | undefined>(undefined);

export const PeriodProvider = ({ children }: { children: ReactNode }) => {
  const { data: periods = [], isLoading: periodsLoading, refetch: refreshPeriods } = usePeriods();
  
  // Load initial state from storage
  const getInitialState = (): PeriodState => {
    const stored = loadFromStorage();
    return {
      selectedPeriod: null,
      selectedYear: null,
      selectedMonth: null,
      reportsYear: null,
      reportsFromMonth: null,
      reportsToMonth: null,
      activeReportTab: stored?.activeReportTab === 'overview' ? 'cash-flow' : (stored?.activeReportTab || 'cash-flow'),
      taxReturnFY: stored?.taxReturnFY || getCurrentFinancialYear(),
    };
  };

  const [state, dispatch] = useReducer(periodReducer, null, getInitialState);

  // Set selected period to the most recent one when periods load
  useEffect(() => {
    if (periods.length > 0 && !state.selectedPeriod) {
      dispatch({ type: 'SET_SELECTED_PERIOD', payload: periods[0].id });
    }
  }, [periods, state.selectedPeriod]);

  // Initialize year/month immediately - no dependency on periods
  useEffect(() => {
    if (state.selectedYear === null) {
      const stored = loadFromStorage();
      
      // Use stored values if available, otherwise use current year/month
      if (stored?.selectedYear && stored?.selectedMonth) {
        dispatch({
          type: 'INITIALIZE_ALL',
          payload: { year: stored.selectedYear, month: stored.selectedMonth }
        });
      } else {
        dispatch({
          type: 'INITIALIZE_ALL',
          payload: { year: currentYear, month: currentMonth }
        });
      }
    }
  }, [state.selectedYear]);

  // Persist state changes to localStorage
  useEffect(() => {
    if (state.selectedYear !== null && state.selectedMonth !== null) {
      saveToStorage({
        selectedYear: state.selectedYear,
        selectedMonth: state.selectedMonth,
        reportsYear: state.reportsYear,
        reportsFromMonth: state.reportsFromMonth,
        reportsToMonth: state.reportsToMonth,
        activeReportTab: state.activeReportTab,
        taxReturnFY: state.taxReturnFY,
      });
    }
  }, [state.selectedYear, state.selectedMonth, state.reportsYear, state.reportsFromMonth, state.reportsToMonth, state.activeReportTab, state.taxReturnFY]);

  return (
    <PeriodContext.Provider value={{ 
      periods,
      periodsLoading,
      refreshPeriods,
      ...state,
      setSelectedPeriod: (id) => dispatch({ type: 'SET_SELECTED_PERIOD', payload: id }),
      setYear: (year) => dispatch({ type: 'SET_YEAR', payload: year }),
      setMonth: (month) => dispatch({ type: 'SET_MONTH', payload: month }),
      setReportsYear: (year) => dispatch({ type: 'SET_REPORTS_YEAR', payload: year }),
      setReportsFromMonth: (month) => dispatch({ type: 'SET_REPORTS_FROM_MONTH', payload: month }),
      setReportsToMonth: (month) => dispatch({ type: 'SET_REPORTS_TO_MONTH', payload: month }),
      setActiveReportTab: (tab) => dispatch({ type: 'SET_ACTIVE_REPORT_TAB', payload: tab }),
      setTaxReturnFY: (fy) => dispatch({ type: 'SET_TAX_RETURN_FY', payload: fy }),
    }}>
      {children}
    </PeriodContext.Provider>
  );
};

export const usePeriod = () => {
  const context = useContext(PeriodContext);
  if (!context) {
    throw new Error("usePeriod must be used within PeriodProvider");
  }
  return context;
};
